import React, { Component } from "react";
import { Navigate } from "react-router-dom";

import LaunchService from "../../services/launch";
import { storeRemoteSession, normalizeKeyboardMode, rememberWebReturn} from "../../context/RemoteLaunchContext";

/**
 * Entry point for the Remote Game Launch flow.
 *
 * The backend (/launch?t=...) burns the launch token, mints a JWT for the
 * launching page's kiosk user, and 302s here with `#sid=...&jwt=...` in the
 * URL fragment (so neither value appears in server logs or Referer headers).
 *
 * We:
 *   1. Read sid + jwt from the fragment
 *   2. Stash the JWT in sessionStorage (so PrivateRoute lets us through)
 *   3. Hydrate the launch session via GET /api/launch/session
 *   4. Persist the session metadata in sessionStorage (used by Home.js to
 *      detect remote-bootstrap mode and by exitGame() to know it's remote)
 *   5. Navigate to /home, where Home.js detects the remote session and
 *      drives the rest of the flow (registration if needed, then game).
 */
class LaunchEntry extends Component {
    constructor(props){
        super(props);
        this.state = {
            redirect: null,
            error: null,
        };
    }

    componentDidMount(){
        this.bootstrap();
    }

    parseFragment(){
        var hash = (window.location.hash || "").replace(/^#/, "");
        var out = {};
        hash.split("&").forEach(function(kv){
            if (!kv) return;
            var eq = kv.indexOf("=");
            if (eq < 0) return;
            out[decodeURIComponent(kv.slice(0, eq))] = decodeURIComponent(kv.slice(eq + 1));
        });
        return out;
    }

    parentOriginFromReferrer(){
        try {
            if (!document.referrer) return null;
            var u = new URL(document.referrer);
            return u.origin;
        } catch (e) { return null; }
    }

    signalErrorAndStop(reason){
        // Always tell the parent something went wrong so it can dismiss us.
        try {
            window.parent.postMessage(
                { type: "pt:gameComplete", result: "error", reason: reason || "launch_failed", at: Date.now() },
                "*"
            );
        } catch (e) { /* ignore */ }
        this.setState({ error: reason || "launch_failed" });
    }

    bootstrap(){
        var fragment = this.parseFragment();
        var sid = fragment.sid;
        var jwt = fragment.jwt;

        if (!sid || !jwt) {
            this.signalErrorAndStop("missing_credentials");
            return;
        }

        // Stash JWT so PrivateRoute and all existing GameService calls work.
        try {
            sessionStorage.setItem("auth", JSON.stringify({ access: jwt, refresh: null }));
        } catch (e) { /* ignore */ }

        // Scrub the fragment from the visible URL.
        try {
            window.history.replaceState(null, "", "/launch-entry");
        } catch (e) { /* ignore */ }

        var parentOrigin = this.parentOriginFromReferrer();

        LaunchService.getSession(sid).then((session) => {
            var serverDisplayOptions = session.display_options || {};
            var displayOptions = {
                keyboard: normalizeKeyboardMode(serverDisplayOptions.keyboard),
                printReceipt: serverDisplayOptions.print_receipt === true,
                // "trusted": the launching page owns identity — the backend
                // already resolved/created the player, so never prompt.
                registrationMode: serverDisplayOptions.registration_mode === "trusted"
                    ? "trusted" : "prompt",
                // "web": arrived via /go/<slug> as a standalone page, so there
                // is no parent frame and the player identifies themselves here.
                // Anything else is the iframe remote-launch flow.
                mode: serverDisplayOptions.mode === "web" ? "web" : "remote",
                // Whether an unrecognised club number may register. Off means
                // they are turned away to the Players Club desk.
                allowNewPlayers: serverDisplayOptions.allow_new_players === true,
                // Where to send a finished player when there is nothing left
                // to play. Empty falls back to the lobby.
                returnUrl: serverDisplayOptions.return_url || "",
                // Tiled faintly behind the centred column in web mode.
                venueLogoUrl: serverDisplayOptions.venue_logo_url || "",
                // WEB-410 -- the way back in when this session dies.
                webSlug: serverDisplayOptions.web_slug || "",
                venueName: serverDisplayOptions.venue_name || "",
            };

            // Store everything Home.js needs to drive the remote-bootstrap.
            storeRemoteSession({
                isRemote: true,
                sessionId: sid,
                parentOrigin: parentOrigin,
                identifierType: session.identifier_type || "card",
                cardNumber: session.card_number,
                clubNumber: session.club_number || "",
                venueId: session.venue_id,
                player: session.player,           // null when new player
                prefill: session.prefill || {},
                promoId: session.promo_id || null,
                displayOptions: displayOptions,
            });

            // WEB-410 -- outlives the tab, so a player coming back tomorrow
            // gets "play again" instead of the kiosk login.
            rememberWebReturn({
                slug: serverDisplayOptions.web_slug || "",
                venueName: serverDisplayOptions.venue_name || "",
                returnUrl: serverDisplayOptions.return_url || "",
            });

            this.setState({ redirect: "/home" });
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.log("Launch session hydration failed:", err);
            this.signalErrorAndStop("session_hydration_failed");
        });
    }

    render(){
        if (this.state.redirect) {
            return <Navigate to={this.state.redirect} replace />;
        }
        if (this.state.error) {
            return (
                <div style={{
                    fontFamily: "-apple-system, system-ui, sans-serif",
                    background: "#111", color: "#eee",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    height: "100vh", margin: 0, padding: "1.5rem",
                    boxSizing: "border-box", textAlign: "center"
                }}>
                    <div style={{ maxWidth: "28rem" }}>
                        <h1 style={{ fontSize: "1.25rem", margin: "0 0 0.5rem" }}>Launch failed</h1>
                        <p style={{ margin: 0, opacity: 0.85 }}>This launch could not be started. Please reload the launching page.</p>
                    </div>
                </div>
            );
        }
        return (
            <div style={{
                fontFamily: "-apple-system, system-ui, sans-serif",
                background: "#111", color: "#eee",
                display: "flex", alignItems: "center", justifyContent: "center",
                height: "100vh", margin: 0
            }}>
                <p>Loading game…</p>
            </div>
        );
    }
}

export default LaunchEntry;
